import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Card, CardContent, CardHeader } from "../../ui/card";
import { Button } from "../../ui/button";
import { Badge } from "../../ui/badge";
import { Input } from "../../ui/input";

import {
  ChevronRight,
  Search,
  ChevronLeft,
  ChevronsLeft,
  ChevronsRight,
  User,
  Clock,
  RotateCcw,
} from "lucide-react";
import { SiAmazon, SiKubernetes } from "react-icons/si";

import {
  Resource,
  getGCPVMList,
  getGCPStorageList,
  getGCPDatabaseList,
  getGCPUserProjectAccess,
  getAWSKubernetesList,
  getAWSUserProjectAccess,
  getAzureVMList,
  getAzureStorageList,
  getAzureDatabaseList,
  getAzureKubernetesList,
  getAzureUserProjectAccess,
  getAWSEC2List,
  getAWSS3List,
  getAWSRDSList,
  getGCPKubernetesList,
  getGCPRolesList,
  getAzureUserConfig,
  getGCPUserConfig,
  getAzureRoleList,
  UserProjectAccess,
  CLOUD_REFRESH_INTERVAL_MS,
  getResourceTypes,
  getAWSPoliciesList,
  formatTime,
  haveSameRoles,
} from "./Data";
import { DetailsDialog } from "../Cloud/DetailsDialog";
import RequestCloudAccessDialog from "./RequestCloudAccessDialog";
import { CloudUsersPageProps } from "../Cloud/interface";
import TenantCloudConfigureDialog from "./TenantCloudConfigureDialog";
import useUserStore from "../../../services/userStore";
import useNotificationsStore from "../../../services/notificationStore";
import { configureGCPUser } from "./Api/tenantgcpapi";
import { myLocalStorage } from "../../StorageHelper";
import { configureUser } from "./Api/tenantazureapi";
import { FaGoogle } from "react-icons/fa6";
import { VscAzure } from "react-icons/vsc";
import { configureAWSUser, fetchUserConfiguration } from "./Api/tenantawsapi";
import { revokeRequest } from "../ResourceRequests/Api/resourcerequestapi";
import EmptyStateCard from "../EmptyStateCard";
import CloudFilters from "../CloudFilters";
import {
  readPersistedListState,
  scopedListStateKey,
  SCOPED_LIST_STATE_PREFIX,
  writePersistedListState,
  parsePersistedNumber,
  parsePersistedString,
  parsePersistedNullableString,
} from "../../../utils/listPaginationState";
import { getCloudAccountSelection } from "../cloudAccountSelection";
import useCloudSyncStore from "../../../services/useCloudSyncStore";
import "../Cloud/CloudStatus.css";

const ITEMS_PER_PAGE = 12;
const SHARED_RESOURCE_SELECTION_KEY = "tenantCloudResourceSelection";

const cloudConfig: Record<string, { icon: any; name: string; fullName: string; label: string }> = {
  gcp: { icon: FaGoogle, name: "GCP", fullName: "Google Cloud Platform", label: "GCP" },
  aws: { icon: SiAmazon, name: "AWS", fullName: "Amazon Web Services", label: "AWS" },
  azure: { icon: VscAzure, name: "Azure", fullName: "Microsoft Azure", label: "Azure" },
};

type ResourceCategory = "compute" | "storage" | "database" | "kubernetes";

const RESOURCE_TYPE_CATEGORY: Record<string, ResourceCategory> = {
  vm: "compute",
  ec2: "compute",
  storage: "storage",
  s3: "storage",
  database: "database",
  rds: "database",
  kubernetes: "kubernetes",
};

const getProviderResourceType = (category: ResourceCategory, cloudProvider: string) => {
  const provider = cloudProvider?.toLowerCase();

  if (category === "kubernetes") return "kubernetes";
  if (provider === "aws") {
    if (category === "storage") return "s3";
    if (category === "database") return "rds";
    return "ec2";
  }

  if (category === "storage") return "storage";
  if (category === "database") return "database";
  return "vm";
};

const normalizeResourceTypeForCloud = (resourceType: string | null, cloudProvider: string) => {
  const category = resourceType ? RESOURCE_TYPE_CATEGORY[resourceType] : undefined;
  return getProviderResourceType(category || "compute", cloudProvider);
};


export default function TenantCloudResources({ cloudProvider }: CloudUsersPageProps) {
  const previousCloudProviderRef = useRef(cloudProvider);
  const activeCloudRef = useRef(cloudProvider);
  const resourceRequestRef = useRef(0);
  const applicableRolesRequestRef = useRef(0);
  const applicableRolesLoadedRef = useRef(false);
  activeCloudRef.current = cloudProvider;
  const cloudStateKey = scopedListStateKey(SCOPED_LIST_STATE_PREFIX.tenantCloudResources, cloudProvider);
  const savedCloudState = readPersistedListState(cloudStateKey);
  const savedSharedResourceState = readPersistedListState(SHARED_RESOURCE_SELECTION_KEY);
  const persistedResourceType =
    parsePersistedNullableString(savedSharedResourceState.selectedResourceType) ||
    parsePersistedNullableString(savedCloudState.selectedResourceType);
  const [selectedTenant, setSelectedTenant] = useState("");

  const [selectedResourceType, setSelectedResourceType] = useState<string | null>(
    persistedResourceType === "kubernetes"
      ? null
      : normalizeResourceTypeForCloud(persistedResourceType, cloudProvider),
  );
  const [mainTab, setMainTab] = useState<"cloud" | "kubernetes">(
    persistedResourceType === "kubernetes" ? "kubernetes" : "cloud"
  );
  const [searchQuery, setSearchQuery] = useState(parsePersistedString(savedCloudState.searchQuery));
  const [currentPage, setCurrentPage] = useState(parsePersistedNumber(savedCloudState.currentPage, 1));
  const [showAllRoles, setShowAllRoles] = useState(false);
  const [requestAccessDialogOpen, setRequestAccessDialogOpen] = useState(false);
  const [requestAccessResource, setRequestAccessResource] = useState<Resource | null>(null);
  const [cloudProviderChanges, setCloudProviderChanges] = useState<any>();
  const [currentResources, setCurrentResources] = useState<any[]>([]);
  const [availableRoles, setAvailableRoles] = useState<any[]>([]);
  const [isApplicableRolesLoading, setIsApplicableRolesLoading] = useState(false);
  const [isConfigured, setIsConfigured] = useState(false);
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [configurationRevision, setConfigurationRevision] = useState(0);
  const [cloudConfigData, setCloudConfigData] = useState<any>({
    azureDisplayName: "",
    azureId: "",
    gcpUserName: "",
    gcpAccountEmail: "",
    awsCredentials: [], // { id, username, arn }
  });
  const [userId, setUserId] = useState("");
  const [userName, setUserName] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [projectId, setProjectId] = useState("");
  const { email } = useUserStore((state) => state.user);
  const [userAccess, setUserAccess] = useState<UserProjectAccess | null>(null);
  const [userAccessLoading, setUserAccessLoading] = useState(false);
  const {
    userAzureNotifications,
    userAzureApprovedNotifications,
    userGcpNotifications,
    userGCPApprovedNotifications,
    userAWSNotifications,
    userAWSApprovedNotifications,
    setUserAWSNotifications,
    setUserAzureNotifications,
    setUserGcpNotifications,
    setUserAWSApprovedNotifications,
    setUserAzureApprovedNotifications,
    setUserGCPApprovedNotifications,
  } = useNotificationsStore();
  const [revokingAccessIds, setRevokingAccessIds] = useState<Set<string>>(new Set());
  const awsAccessRevision = useMemo(() => cloudProvider === "aws"
    ? userAWSApprovedNotifications
      .map((notification: any) => `${notification.id}:${notification.requestStatus}:${notification.isPolicyAdded}:${notification.generatedPoliciesARN || ""}`)
      .sort()
      .join("|")
    : "", [cloudProvider, userAWSApprovedNotifications]);

  const [nowTick, setNowTick] = useState(() => Date.now());


  const config = cloudConfig[cloudProvider];
  const CloudIcon = config.icon;
  const allResourceTypes = getResourceTypes(cloudProvider);
  const cloudResourceTypes = allResourceTypes.filter((type) => type.id !== "kubernetes");
  const normalizedSelectedResourceType =
    mainTab === "kubernetes" ? null : normalizeResourceTypeForCloud(selectedResourceType, cloudProvider);
  const activeResourceType = mainTab === "kubernetes" ? "kubernetes" : normalizedSelectedResourceType;
  const accountSelection = getCloudAccountSelection(cloudProvider);
  const hasCloudAccount = accountSelection.configured;

  useEffect(() => {
    const timer = setInterval(() => {
      setNowTick(Date.now());
    }, 1000);

    return () => clearInterval(timer);
  }, []);



  const handleCloudProvider = (value: any) => {
    setCloudProviderChanges(value);
  };


  const getCurrentProjectResources = useCallback(async () => {
    if (!getCloudAccountSelection(cloudProvider).configured) return [];

    if (cloudProvider === "gcp") {
      const [vmList, storageList, dbList, kubernetes] = await Promise.all([
        getGCPVMList(),
        getGCPStorageList(),
        getGCPDatabaseList(),
        getGCPKubernetesList()
      ]);
      return [...vmList, ...storageList, ...dbList, ...kubernetes];
    } else if (cloudProvider === "aws") {
      const [vmList, storageList, dbList, kubernetes] = await Promise.all([
        getAWSEC2List(),
        getAWSS3List(),
        getAWSRDSList(),
        getAWSKubernetesList()
      ]);
      return [...vmList, ...storageList, ...dbList, ...kubernetes];
    } else {
      const [vmList, storageList, dbList, kubernetes] = await Promise.all([
        getAzureVMList(),
        getAzureStorageList(),
        getAzureDatabaseList(),
        getAzureKubernetesList(),
      ]);
      return [...vmList, ...storageList, ...dbList, ...kubernetes];
    }
  }, [cloudProvider]);


  const getApplicableRoles = useCallback(async () => {
    if (!getCloudAccountSelection(cloudProvider).configured) return [];

    if (activeResourceType) {
      if (cloudProvider === "gcp") {
        const applicableRoles = await getGCPRolesList(activeResourceType || "");
        return [...applicableRoles]
      } else if (cloudProvider === "azure") {
        if (requestAccessResource) {
          const applicableRoles = await getAzureRoleList(activeResourceType || "", userId, requestAccessResource?.id);
          return [...applicableRoles]
        }
      } else if (cloudProvider === "aws") {
        const applicableRoles = await getAWSPoliciesList(
          projectId,
          activeResourceType
        );
        return [...applicableRoles]
      }
    }

    return [];
  }, [activeResourceType, cloudProvider, projectId, requestAccessResource, userId])

  const refetchResources = useCallback(async (options?: { live?: boolean }) => {
    if (activeCloudRef.current !== cloudProvider) return;

    const requestId = ++resourceRequestRef.current;
    if (!getCloudAccountSelection(cloudProvider).configured) {
      setCurrentResources([]);
      return;
    }
    const preserveEmptyResult = options?.live && useCloudSyncStore.getState().syncStatus[cloudProvider];
    try {
      const resources = await getCurrentProjectResources();
      if (resourceRequestRef.current !== requestId || activeCloudRef.current !== cloudProvider) return;
      if (preserveEmptyResult && resources.length === 0) return;
      setCurrentResources(resources);
    } catch (error) {
      console.error("Error fetching project resources:", error);
    }
  }, [cloudProvider, getCurrentProjectResources]);

  const refetchUserAccess = useCallback(async (options?: { live?: boolean }) => {
    if (!hasCloudAccount) {
      setUserAccess(null);
      setUserAccessLoading(false);
      return;
    }
    if (!options?.live) {
      setUserAccessLoading(true);
    }
    try {
      let result: UserProjectAccess | null = null;

      if (cloudProvider === "gcp" && projectId && userEmail) {
        result = await getGCPUserProjectAccess(projectId, userEmail);
      } else if (cloudProvider === "aws" && userEmail) {
        result = await getAWSUserProjectAccess(userEmail);
      } else if (cloudProvider === "azure" && userId) {
        result = await getAzureUserProjectAccess(userId);
      } else {
        setUserAccess(null);
        return;
      }

      setUserAccess(result);
    } catch (error) {
      console.error("Error fetching user access:", error);
      if (!options?.live) {
        setUserAccess(null);
      }
    } finally {
      if (!options?.live) {
        setUserAccessLoading(false);
      }
    }
  }, [cloudProvider, hasCloudAccount, projectId, userEmail, userId]);

  const refetchApplicableRoles = useCallback(async (options?: { live?: boolean }) => {
    if (!requestAccessDialogOpen) {
      applicableRolesLoadedRef.current = false;
      setAvailableRoles((current) => current.length === 0 ? current : []);
      setIsApplicableRolesLoading(false);
      return;
    }
    const isInitialLoad = !applicableRolesLoadedRef.current;
    if (options?.live && isInitialLoad) return;

    const requestId = ++applicableRolesRequestRef.current;
    if (isInitialLoad) {
      setAvailableRoles([]);
      setIsApplicableRolesLoading(true);
    }
    try {
      const roles: any[] = await getApplicableRoles();
      if (applicableRolesRequestRef.current !== requestId || activeCloudRef.current !== cloudProvider) return;
      applicableRolesLoadedRef.current = true;
      setAvailableRoles((current) => haveSameRoles(current, roles) ? current : roles);
    } catch (error) {
      if (applicableRolesRequestRef.current !== requestId || activeCloudRef.current !== cloudProvider) return;
      if (isInitialLoad) {
        applicableRolesLoadedRef.current = true;
        setAvailableRoles([]);
      }
      console.error("Error fetching applicable roles:", error);
    } finally {
      if (
        isInitialLoad &&
        applicableRolesRequestRef.current === requestId &&
        activeCloudRef.current === cloudProvider
      ) {
        setIsApplicableRolesLoading(false);
      }
    }
  }, [cloudProvider, getApplicableRoles, requestAccessDialogOpen]);

  useEffect(() => {
    refetchUserAccess();
  }, [refetchUserAccess, cloudProviderChanges, awsAccessRevision]);

  const filteredResourceTypes = cloudResourceTypes;

  const typeFilteredResources = mainTab === "kubernetes"
    ? currentResources.filter((r) => r.type === "kubernetes")
    : normalizedSelectedResourceType
      ? currentResources.filter((r) => r.type === normalizedSelectedResourceType)
      : currentResources.filter((r) => r.type !== "kubernetes");

  const filteredResources = searchQuery
    ? typeFilteredResources.filter((r) =>
      (r.name || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
      (r.region || "").toLowerCase().includes(searchQuery.toLowerCase())
    )
    : typeFilteredResources;

  const totalPages = Math.ceil(filteredResources.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedResources = filteredResources.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  const handleFilterChange = (type: string | null) => {
    setSelectedResourceType(type);
    setMainTab("cloud");
    setCurrentPage(1);
  };

  const handleMainTabChange = (tab: "cloud" | "kubernetes") => {
    setMainTab(tab);
    setCurrentPage(1);
    if (tab === "kubernetes") {
      setSelectedResourceType(null);
      return;
    }
    if (!selectedResourceType) {
      setSelectedResourceType(normalizeResourceTypeForCloud(null, cloudProvider));
    }
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setCurrentPage(1);
  };

  useEffect(() => {
    const persistedSelectedResourceType = mainTab === "kubernetes" ? "kubernetes" : normalizedSelectedResourceType;

    writePersistedListState(cloudStateKey, {
      currentPage,
      selectedResourceType: persistedSelectedResourceType,
      searchQuery,
    });
    writePersistedListState(SHARED_RESOURCE_SELECTION_KEY, {
      selectedResourceType: persistedSelectedResourceType,
    });
  }, [cloudStateKey, currentPage, mainTab, normalizedSelectedResourceType, searchQuery]);

  const getResourceTypeIcon = (type: string) => {
    const resourceType = allResourceTypes.find((rt) => rt.id === type);
    return resourceType || allResourceTypes[0];
  };

  const parseAccessExpiry = (value: any) => {
    if (!value) return null;
    const timestamp =
      typeof value === "string" && !/[zZ]|[+-]\d{2}:?\d{2}$/.test(value)
        ? `${value}Z`
        : value;
    const date = new Date(timestamp);
    return Number.isNaN(date.getTime()) ? null : date;
  };

  const isPermanentAccess = (accessRecord: any) => {
    const privilegeLevel =
      accessRecord?.privilegeLevel ??
      accessRecord?.privilegeType ??
      accessRecord?.privilege ??
      accessRecord?.accessPrivilege;

    return (
      (typeof privilegeLevel === "string" && privilegeLevel.toUpperCase() === "PERMANENT") ||
      accessRecord?.selectedTime === 0 ||
      accessRecord?.expiryTimeAmount === 0
    );
  };

  const isAccessActive = (accessRecord: any) => {
    if (!accessRecord) return false;
    if (isPermanentAccess(accessRecord)) return true;

    const expiry =
      cloudProvider === "gcp" || cloudProvider === "aws"
        ? accessRecord?.accessTimeOverAt
        : accessRecord?.validTo;
    const expiryDate = parseAccessExpiry(expiry);

    return expiryDate ? expiryDate.getTime() > nowTick : false;
  };

  const fetchAccessGranted = (type: string, resource: any) => {

    if (cloudProvider === "azure") {
      let scopeType = type === "vm" ? "VIRTUAL_MACHINE" :
        type === "storage" ? "STORAGE_ACCOUNT" : type === "kubernetes" ? "AZURE_KUBERNETES" : "DATABASE";
      let azureVM = userAzureApprovedNotifications?.filter(
        (userAzureNotification: any) =>
          userAzureNotification.scopeType === scopeType &&
          userAzureNotification.status === "APPROVED" &&
          userAzureNotification.resourceName === resource.name &&
          isAccessActive(userAzureNotification)
        ,
      );
      return azureVM && azureVM
    } else if (cloudProvider === "gcp") {
      const fetchResourceLevel = userGCPApprovedNotifications?.filter(
        (userGCPNotification: any) =>
          userGCPNotification.accessType === "resources" &&
          userGCPNotification.requestStatus === "APPROVED" &&
          userGCPNotification.accessName === resource.name &&
          isAccessActive(userGCPNotification)
      );
      return fetchResourceLevel && fetchResourceLevel
    } else if (cloudProvider === "aws") {
      const fetchResourceLevel = userAWSApprovedNotifications?.filter(
        (userAWSNotification: any) =>
          userAWSNotification.accessType === "resources" &&
          userAWSNotification.requestStatus === "APPROVED" &&
          userAWSNotification.accessName === resource.name &&
          isAccessActive(userAWSNotification)
      );
      return fetchResourceLevel && fetchResourceLevel
    }
  }

  const fetchPendingRequest = (type: string, resource: any) => {
    if (cloudProvider === "azure") {
      const scopeType = type === "vm" ? "VIRTUAL_MACHINE" :
        type === "storage" ? "STORAGE_ACCOUNT" : type === "kubernetes" ? "AZURE_KUBERNETES" : "DATABASE";
      return userAzureNotifications?.filter(
        (userAzureNotification: any) =>
          userAzureNotification.scopeType === scopeType &&
          userAzureNotification.status === "PENDING" &&
          userAzureNotification.resourceName === resource.name
      ) || [];
    }
    if (cloudProvider === "gcp") {
      return userGcpNotifications?.filter(
        (userGCPNotification: any) =>
          userGCPNotification.accessType === "resources" &&
          userGCPNotification.requestStatus === "PENDING" &&
          userGCPNotification.accessName === resource.name
      ) || [];
    }
    if (cloudProvider === "aws") {
      return userAWSNotifications?.filter(
        (userAWSNotification: any) =>
          userAWSNotification.accessType === "resources" &&
          userAWSNotification.requestStatus === "PENDING" &&
          userAWSNotification.accessName === resource.name
      ) || [];
    }
    return [];
  };

  const handleRevokeAccess = async (accessRecords: any[], resourceId: string) => {
    const activeRecords = accessRecords.filter((record) => record?.id != null);
    if (activeRecords.length === 0) return;
    setRevokingAccessIds((current) => new Set(current).add(resourceId));
    try {
      for (const record of activeRecords) {
        const revoked = await revokeRequest(cloudProvider, record.id);
        if (revoked === false) {
          throw new Error("Failed to revoke cloud access");
        }
      }
      const revokedIds = new Set(activeRecords.map((record) => String(record.id)));
      if (cloudProvider === "aws") {
        setUserAWSApprovedNotifications(userAWSApprovedNotifications.filter((record: any) => !revokedIds.has(String(record.id))));
        setUserAWSNotifications(userAWSNotifications.filter((record: any) => !revokedIds.has(String(record.id))));
      } else if (cloudProvider === "azure") {
        setUserAzureApprovedNotifications(userAzureApprovedNotifications.filter((record: any) => !revokedIds.has(String(record.id))));
        setUserAzureNotifications(userAzureNotifications.filter((record: any) => !revokedIds.has(String(record.id))));
      } else {
        setUserGCPApprovedNotifications(userGCPApprovedNotifications.filter((record: any) => !revokedIds.has(String(record.id))));
        setUserGcpNotifications(userGcpNotifications.filter((record: any) => !revokedIds.has(String(record.id))));
      }
    } catch (error) {
      console.error(error);
    } finally {
      setRevokingAccessIds((current) => {
        const next = new Set(current);
        next.delete(resourceId);
        return next;
      });
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "running":
        return "bg-green-500/10 text-green-600 dark:text-green-400";
      case "stopped":
        return "bg-red-500/10 text-red-600 dark:text-red-400";
      case "available":
        return "bg-blue-500/10 text-blue-600 dark:text-blue-400";
      default:
        return "bg-gray-500/10 text-gray-600 dark:text-gray-400";
    }
  };

  const getResourceTypeLabel = (type: string) => {
    const typeInfo = getResourceTypeIcon(type);
    return `${config.label} ${typeInfo.name}`;
  };


  const getUserCloudConfig = async (cloudProvider: string) => {
    if (!getCloudAccountSelection(cloudProvider).configured && cloudProvider !== "aws") {
      setUserId("");
      setUserName("");
      setUserEmail("");
      setProjectId("");
      setUserAccess(null);
      setIsConfigured(false);
      setConfigDialogOpen(false);
      return;
    }

    if (cloudProvider === "gcp") {
      let response = await getGCPUserConfig(email);
      if (response) {
        let { gcpUserName, gcpAccountEmail, projectId } = response;
        if (gcpAccountEmail) {
          setUserName(gcpUserName)
          setUserEmail(gcpAccountEmail)
          setIsConfigured(true)
          setConfigDialogOpen(false)
          setProjectId(projectId)
        } else {
          setUserName("")
          setUserEmail("")
          setIsConfigured(false)
          setConfigDialogOpen(true)
        }
      } else {
        setUserName("")
        setUserEmail("")
        setIsConfigured(false)
        setConfigDialogOpen(true)
      }
    } else if (cloudProvider === "aws") {
      const latestTenant = myLocalStorage.getItem("latestTenant")?.tenantName;
      const selectedAwsAccount = myLocalStorage.getItem("selectedAwsAccount");
      let response = await fetchUserConfiguration(latestTenant, email)
      if (response && Array.isArray(response)) {
        let filteredResponse: any;
        if (selectedAwsAccount) {
          filteredResponse = response.find((item: any) => item.awsAccountId === selectedAwsAccount);
        } else {
          filteredResponse = response[0];
        }
        if (filteredResponse?.awsUserName && filteredResponse?.awsAccountARN) {
          let { awsUserName, awsAccountARN, awsAccountId } = filteredResponse;
          myLocalStorage.setItem("selectedAwsAccount", awsAccountId);
          setUserName(awsUserName)
          setUserEmail(awsAccountARN)
          setIsConfigured(true)
          setConfigDialogOpen(false)
          setProjectId(awsAccountId)
        } else {
          setUserName("")
          setUserEmail("")
          setIsConfigured(false)
          setConfigDialogOpen(true)
        }
      } else if (response) {
        let { awsUserName, awsAccountARN, awsAccountId } = response;
        if (awsUserName && awsAccountARN) {
          setUserName(awsUserName)
          setUserEmail(awsAccountARN)
          setIsConfigured(true)
          setConfigDialogOpen(false)
          setProjectId(awsAccountId)
        } else {
          setUserName("")
          setUserEmail("")
          setIsConfigured(false)
          setConfigDialogOpen(true)
        }
      } else {
        setUserName("")
        setUserEmail("")
        setIsConfigured(false)
        setConfigDialogOpen(true)
      }
    } else {
      try {
        let response = await getAzureUserConfig(email);
        if (response) {
          let { azureId } = response;
          setUserId(azureId);
          setIsConfigured(true)
          setConfigDialogOpen(false)
        } else {
          setIsConfigured(false)
          setConfigDialogOpen(true)
        }
      } catch (e) {
        setIsConfigured(false)
        setConfigDialogOpen(true)
      }

    }
  }

  const applyConfiguredUser = (response: any) => {
    if (cloudProvider === "gcp" && response?.gcpAccountEmail) {
      setUserName(response.gcpUserName || "");
      setUserEmail(response.gcpAccountEmail);
      setProjectId(response.projectId || "");
      return {
        userId: "",
        userEmail: response.gcpAccountEmail,
        projectId: response.projectId || "",
      };
    }

    if (cloudProvider === "azure" && response?.azureId) {
      setUserId(response.azureId);
      return { userId: response.azureId, userEmail: "", projectId: "" };
    }

    if (cloudProvider === "aws") {
      const configurations = Array.isArray(response) ? response : response ? [response] : [];
      const selectedAccount = myLocalStorage.getItem("selectedAwsAccount");
      const userConfig = selectedAccount
        ? configurations.find((item: any) => item.awsAccountId === selectedAccount) || configurations[0]
        : configurations[0];
      if (userConfig?.awsUserName && userConfig?.awsAccountARN && userConfig?.awsAccountId) {
        myLocalStorage.setItem("selectedAwsAccount", userConfig.awsAccountId);
        setUserName(userConfig.awsUserName);
        setUserEmail(userConfig.awsAccountARN);
        setProjectId(userConfig.awsAccountId);
        return {
          userId: "",
          userEmail: userConfig.awsAccountARN,
          projectId: userConfig.awsAccountId,
        };
      }
    }

    return null;
  };

  const refreshConfiguredUserAccess = async (identity: { userId: string; userEmail: string; projectId: string }) => {
    try {
      let result: UserProjectAccess | null = null;
      if (cloudProvider === "gcp") {
        result = await getGCPUserProjectAccess(identity.projectId, identity.userEmail);
      } else if (cloudProvider === "aws") {
        result = await getAWSUserProjectAccess(identity.userEmail);
      } else {
        result = await getAzureUserProjectAccess(identity.userId);
      }
      setUserAccess(result);
    } catch {
      setUserAccess(null);
    }
  };

  const submitUserReg = async (): Promise<boolean> => {
    const latestTenant = myLocalStorage.getItem("latestTenant")?.tenantName;
    try {
      let response: any;
      if (cloudProvider === "gcp") {
        response = await configureGCPUser(latestTenant, email, cloudConfigData?.gcpUserName || "", cloudConfigData?.gcpAccountEmail);
      } else if (cloudProvider === "azure") {
        const payload = {
          azureId: cloudConfigData.azureId,
          displayName: cloudConfigData.displayName,
          wsTenantName: latestTenant,
          email: email,
        };
        response = await configureUser(payload);
      } else {
        const payload = {
          tenantName: latestTenant,
          tenantEmail: email,
          users: cloudConfigData.awsCredentials
        };
        response = await configureAWSUser(payload);
      }

      const identity = applyConfiguredUser(response);
      if (!identity) return false;
      void Promise.allSettled([
        refetchResources(),
        refreshConfiguredUserAccess(identity),
      ]);
      return true;
    } catch {
      return false;
    }
  };

  const handleConfigured = () => {
    setIsConfigured(true);
    setConfigurationRevision((revision) => revision + 1);
  };




  useEffect(() => {
    refetchResources();
  }, [cloudProvider, selectedTenant, normalizedSelectedResourceType, cloudProviderChanges, refetchResources]);



  useEffect(() => {
    refetchApplicableRoles();
  }, [refetchApplicableRoles]);

  useEffect(() => {
    if (!hasCloudAccount || !isConfigured) return;
    let refreshing = false;
    const refresh = async () => {
      if (refreshing) return;
      refreshing = true;
      try {
        await Promise.all([
          refetchResources({ live: true }),
          refetchUserAccess({ live: true }),
          refetchApplicableRoles({ live: true }),
        ]);
      } finally {
        refreshing = false;
      }
    };
    const timer = setInterval(refresh, CLOUD_REFRESH_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [hasCloudAccount, isConfigured, refetchApplicableRoles, refetchResources, refetchUserAccess]);




  useEffect(() => {
    if (previousCloudProviderRef.current === cloudProvider) {
      return;
    }

    previousCloudProviderRef.current = cloudProvider;
    setCurrentPage(1);
    setSelectedResourceType((currentType) =>
      mainTab === "kubernetes" ? null : normalizeResourceTypeForCloud(currentType, cloudProvider),
    );
  }, [cloudProvider, mainTab]);


  useEffect(() => {
    const checkUserConfig = async () => {
      if (!hasCloudAccount && cloudProvider !== "aws") {
        setUserId("");
        setUserName("");
        setUserEmail("");
        setProjectId("");
        setUserAccess(null);
        setIsConfigured(false);
        setConfigDialogOpen(false);
        return;
      }
      if (cloudProvider === "aws" && !hasCloudAccount) {
        setConfigDialogOpen(true);
      }
      try {
        await getUserCloudConfig(cloudProvider);
      } catch (err) {
        console.error("Error checking user config:", err);
        setIsConfigured(false);
        setConfigDialogOpen(true);
      }
    };
    checkUserConfig();
  }, [cloudProvider, email, cloudProviderChanges, selectedTenant, hasCloudAccount]);

  const userAccessRoles = Array.isArray(userAccess?.roles) ? userAccess.roles : [];
  const visibleUserRoles = showAllRoles ? userAccessRoles : userAccessRoles.slice(0, 3);
  const userAccessDisplayName = userAccess?.userName || userName || "Loading user";
  const userAccessEmail = userAccess?.accountEmail || userEmail || email || "Loading account";

  if (!hasCloudAccount && cloudProvider !== "aws") {
    return (
      <div className="h-full flex flex-col bg-background overflow-auto">
        <div className="p-6 space-y-6">
          <Card className="cloud-card-surface rounded-2xl">
            <CardContent className="p-5 flex flex-col lg:flex-row lg:items-center justify-between gap-4">
              <div className="flex items-center gap-4 min-w-0">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <CloudIcon className="w-6 h-6 text-primary" />
                </div>
                <div className="min-w-0">
                  <h1 className="text-xl font-semibold text-foreground">{config.name} Resources</h1>
                  <p className="text-sm text-muted-foreground">{accountSelection.description}</p>
                </div>
              </div>
              <CloudFilters
                key={`${cloudProvider}-${configurationRevision}`}
                cloudProvider={cloudProvider}
                onTenantChange={setSelectedTenant}
                onCloudAccountChange={handleCloudProvider}
                isAdmin={false}
                hideTenant
                inline
              />
            </CardContent>
          </Card>

          <EmptyStateCard
            icon={CloudIcon}
            title={accountSelection.title}
            description={accountSelection.description}
            action={
              <Button onClick={() => setConfigDialogOpen(true)}>
                Configure
              </Button>
            }
          />

          {configDialogOpen && (
            <TenantCloudConfigureDialog
              open={configDialogOpen}
              onOpenChange={setConfigDialogOpen}
              cloudProvider={cloudProvider}
              config={config}
              cloudConfigData={cloudConfigData}
              setCloudConfigData={setCloudConfigData}
              onConfigured={handleConfigured}
              subUserReg={submitUserReg}
            />
          )}
        </div>
      </div>
    );
  }


  if (!isConfigured) {
    return (
      <div className="h-full flex flex-col bg-background overflow-auto">
        <div className="p-6 space-y-6">
          <Card className="cloud-card-surface rounded-2xl">
            <CardContent className="p-5 flex flex-col lg:flex-row lg:items-center justify-between gap-4">
              <div className="flex items-center gap-4 min-w-0">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <CloudIcon className="w-6 h-6 text-primary" />
                </div>
                <div className="min-w-0">
                  <h1 className="text-xl font-semibold text-foreground">{config.name} Resources</h1>
                  <p className="text-sm text-muted-foreground">Configure your cloud identity before requesting access.</p>
                </div>
              </div>
              <CloudFilters
                key={`${cloudProvider}-${configurationRevision}`}
                cloudProvider={cloudProvider}
                onTenantChange={setSelectedTenant}
                onCloudAccountChange={handleCloudProvider}
                isAdmin={false}
                hideTenant
                inline
              />
            </CardContent>
          </Card>

          <EmptyStateCard
            icon={CloudIcon}
            title="No User Configured"
            description={`Please configure your ${config.name} account first.`}
            action={
              <Button onClick={() => setConfigDialogOpen(true)}>
                Configure
              </Button>
            }
          />

          {configDialogOpen && (
            <TenantCloudConfigureDialog
              open={configDialogOpen}
              onOpenChange={setConfigDialogOpen}
              cloudProvider={cloudProvider}
              config={config}
              cloudConfigData={cloudConfigData}
              setCloudConfigData={setCloudConfigData}
              onConfigured={handleConfigured}
              subUserReg={submitUserReg}
            />
          )}
        </div>
      </div>

    );
  }

  return (
    <div className="h-full flex flex-col bg-background overflow-auto">
      <div className="p-6 space-y-6">
        <Card className="cloud-card-surface rounded-2xl" data-testid="card-user-access">
          <CardContent className="p-5">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
              <div className="flex items-start gap-3 min-w-0 flex-1">
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <User className="w-5 h-5 text-primary" />
                </div>
                <div className="min-w-0">
                  <div className="font-semibold text-sm text-foreground truncate">{userAccessDisplayName}</div>
                  <div className="text-xs text-muted-foreground truncate">{userAccessEmail}</div>
                  <div className="mt-3 flex items-start gap-2">
                    <span className="text-xs text-muted-foreground whitespace-nowrap pt-1">Roles:</span>
                    <div className="flex flex-wrap items-center gap-1.5">
                      {userAccessLoading && userAccessRoles.length === 0 ? (
                        <Badge variant="secondary" className="text-xs rounded-md">
                          Loading roles...
                        </Badge>
                      ) : visibleUserRoles.length > 0 ? (
                        visibleUserRoles.map((role, index) => (
                          <Badge key={index} variant="secondary" className="font-mono text-xs rounded-md">
                            {role}
                          </Badge>
                        ))
                      ) : (
                        <Badge variant="secondary" className="text-xs rounded-md">
                          No roles
                        </Badge>
                      )}
                      {userAccessRoles.length > 3 && (
                        <button
                          className="text-xs text-primary hover:underline cursor-pointer"
                          onClick={() => setShowAllRoles(!showAllRoles)}
                          data-testid="button-toggle-roles"
                        >
                          {showAllRoles ? "Show less" : `+${userAccessRoles.length - 3} more`}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <CloudFilters
                key={`${cloudProvider}-${configurationRevision}`}
                cloudProvider={cloudProvider}
                onTenantChange={setSelectedTenant}
                onCloudAccountChange={handleCloudProvider}
                isAdmin={false}
                hideTenant
                inline
              />
            </div>
          </CardContent>
        </Card>

        <Card className="cloud-card-surface rounded-2xl overflow-hidden">
          <div className="flex flex-col md:flex-row md:items-center justify-between p-3 gap-6">
            <div className="flex items-center">
              <div className="cs-tabs !m-0 !w-auto !bg-transparent flex-wrap">
                {filteredResourceTypes.map((type) => {
                  const Icon = type.icon;
                  return (
                    <button
                      key={type.id}
                      onClick={() => handleFilterChange(type.id)}
                      className={`cs-tab ${mainTab === "cloud" && normalizedSelectedResourceType === type.id ? "active" : ""}`}
                      data-testid={`tab-resource-${type.id}`}
                    >
                      <Icon className="w-4 h-4" />
                      {type.name}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="flex items-center">
              <div className="cs-tabs !m-0 !w-auto !bg-transparent">
                <button
                  onClick={() => handleMainTabChange("cloud")}
                  className={`cs-tab ${mainTab === "cloud" ? "active" : ""}`}
                  data-testid="tab-cloud-resources"
                >
                  <CloudIcon className="w-4 h-4" />
                  Cloud Resources
                </button>
                <button
                  onClick={() => handleMainTabChange("kubernetes")}
                  className={`cs-tab ${mainTab === "kubernetes" ? "active" : ""}`}
                  data-testid="tab-kubernetes-resources"
                >
                  <SiKubernetes className="w-4 h-4" />
                  Kubernetes Clusters
                </button>
              </div>
            </div>
          </div>
        </Card>

        <Card className="cloud-card-surface rounded-2xl">
          <CardContent className="p-3 flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder={mainTab === "kubernetes" ? "Search clusters by name or region..." : "Search resources by name or region..."}
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="pl-10 h-11 rounded-xl bg-card border-border/50 shadow-sm focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-border/50 focus:border-border/50"
                data-testid="input-search-resources"
              />
            </div>
            <div className="text-sm text-muted-foreground">
              Showing <span className="font-medium">{paginatedResources.length}</span> of{" "}
              <span className="font-medium">{filteredResources.length}</span>{" "}
              {mainTab === "kubernetes" ? "clusters" : "resources"}
            </div>
          </CardContent>
        </Card>

        {filteredResources.length === 0 ? (
          <Card className="cloud-card-surface border-dashed rounded-2xl">
            <CardContent className="flex flex-col items-center justify-center py-12 text-center">
              <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
                {mainTab === "kubernetes" ? (
                  <SiKubernetes className="w-8 h-8 text-muted-foreground" />
                ) : (
                  <CloudIcon className="w-8 h-8 text-muted-foreground" />
                )}
              </div>
              <h3 className="text-lg font-semibold mb-2">
                {searchQuery ? "No Resources Found" : mainTab === "kubernetes" ? "No Kubernetes Clusters Available" : "No Resources Available"}
              </h3>
              <p className="text-sm text-muted-foreground max-w-md">
                {searchQuery
                  ? "No resources match your search. Try adjusting your search terms."
                  : mainTab === "kubernetes"
                    ? "No Kubernetes clusters are available for this account right now."
                    : "No resources are available for the selected type right now."}
              </p>
            </CardContent>
          </Card>
        ) : (
          <>
            <div className="grid grid-cols-1 items-start gap-4 md:grid-cols-2 lg:grid-cols-3">
              {paginatedResources.map((resource) => {
                const resourceTypeInfo = getResourceTypeIcon(resource.type);
                const Icon = resourceTypeInfo.icon;
                const isHasAccess = fetchAccessGranted(activeResourceType || "", resource);
                const pendingRequests = fetchPendingRequest(activeResourceType || "", resource);
                const isPendingRequest = pendingRequests.length > 0;

                return (
                  <Card
                    key={resource.id}
                    className="cloud-list-card rounded-2xl transition-all flex flex-col"
                    data-testid={`card-resource-${resource.id}`}
                  >
                    <CardHeader className="pb-4">
                      <div className="flex items-start justify-between gap-2">
                        <div
                          className={`w-12 h-12 rounded-md ${resourceTypeInfo.color} flex items-center justify-center flex-shrink-0`}
                        >
                          <Icon className="w-6 h-6" />
                        </div>
                        <Badge
                          variant="secondary"
                          className={`text-xs ${getStatusColor(resource.status)}`}
                        >
                          {resource.status}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="flex flex-col flex-1">
                      <div className="flex-1 space-y-4">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-medium text-sm break-all flex-1" title={resource.name}>
                              {resource.name}
                            </h3>
                            <DetailsDialog
                              data={resource}
                              title={`${resource.name} Details`}
                              trigger="icon"
                              testId={`button-info-${resource.id}`}
                            />
                          </div>
                          <p className="text-xs text-muted-foreground line-clamp-2">
                            {resource.description}
                          </p>
                        </div>

                        <div className="space-y-2 text-xs">
                          <div className="flex justify-between gap-3">
                            <span className="text-muted-foreground">Region:</span>
                            <span className="font-medium text-right">{resource.region || "-"}</span>
                          </div>
                          <div className="flex justify-between gap-3">
                            <span className="text-muted-foreground">Created:</span>
                            <span className="font-medium text-right">
                              {typeof resource.createdAt === "object"
                                ? "-"
                                : resource.createdAt ?? "-"}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="mt-4 pt-4 border-t">
                        {isHasAccess && isHasAccess.length > 0 ? (
                          <div className="space-y-2">
                            <div className="flex items-center justify-center gap-2 p-3 rounded-md bg-green-500/10 border border-green-500/20">
                              <Clock className="w-4 h-4 text-green-600 dark:text-green-400" />
                              <div className="flex flex-col items-center">
                                <span className="text-xs text-muted-foreground">Access Granted</span>
                                <span className="text-sm font-semibold text-green-600 dark:text-green-400 font-mono">
                                  {formatTime(isHasAccess, cloudProvider)}
                                </span>
                              </div>
                            </div>
                            <Button
                              className="w-full gap-2 rounded-xl !border-transparent bg-red-600 text-white shadow-md shadow-red-600/20 hover:bg-red-700 hover:shadow-lg hover:shadow-red-600/30 focus-visible:ring-0"
                              size="sm"
                              disabled={revokingAccessIds.has(String(resource.id))}
                              onClick={() => handleRevokeAccess(isHasAccess, String(resource.id))}
                              data-testid={`button-revoke-access-${resource.id}`}
                            >
                              Revoke
                              <RotateCcw className="w-4 h-4" />
                            </Button>
                          </div>
                        ) : (
                          <Button
                            className={isPendingRequest
                              ? "w-full gap-2 rounded-xl bg-muted text-muted-foreground"
                              : "w-full gap-2 rounded-xl bg-primary text-primary-foreground shadow-md shadow-primary/20 hover:bg-primary/90 hover:shadow-lg hover:shadow-primary/30"}
                            size="sm"
                            variant={isPendingRequest ? "secondary" : "default"}
                            disabled={isPendingRequest}
                            onClick={() => {
                              applicableRolesRequestRef.current += 1;
                              applicableRolesLoadedRef.current = false;
                              setAvailableRoles([]);
                              setIsApplicableRolesLoading(true);
                              setRequestAccessResource(resource);
                              setRequestAccessDialogOpen(true);
                            }}
                            data-testid={`button-request-access-${resource.id}`}
                          >
                            {isPendingRequest ? "Request Pending" : "Request Access"}
                            {!isPendingRequest && <ChevronRight className="w-4 h-4" />}
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {totalPages > 1 && (
              <div className="cloud-card-surface rounded-2xl flex items-center justify-between p-4">
                <div className="text-sm text-muted-foreground">
                  Page {currentPage} of {totalPages}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                    data-testid="button-page-first"
                  >
                    <ChevronsLeft className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(currentPage - 1)}
                    disabled={currentPage === 1}
                    data-testid="button-page-prev"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <div className="flex gap-1">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      return (
                        <Button
                          key={pageNum}
                          variant={currentPage === pageNum ? "default" : "outline"}
                          size="sm"
                          onClick={() => setCurrentPage(pageNum)}
                          className="w-9"
                          data-testid={`button-page-${pageNum}`}
                        >
                          {pageNum}
                        </Button>
                      );
                    })}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    data-testid="button-page-next"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage === totalPages}
                    data-testid="button-page-last"
                  >
                    <ChevronsRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Request Access Dialog */}
      {requestAccessResource &&
        <RequestCloudAccessDialog
          open={requestAccessDialogOpen}
          onOpenChange={(open) => {
            setRequestAccessDialogOpen(open);
            if (!open) {
              applicableRolesRequestRef.current += 1;
              applicableRolesLoadedRef.current = false;
              setAvailableRoles([]);
              setIsApplicableRolesLoading(false);
            }
          }}
          resource={requestAccessResource}
          resourceName={requestAccessResource?.name || ""}
          resourceType={cloudProvider}
          resourceTypeLabel={requestAccessResource ? getResourceTypeLabel(requestAccessResource.type) : undefined}
          availableRoles={availableRoles}
          isRolesLoading={isApplicableRolesLoading}
          from={activeResourceType || ""}
          userId={userId}
          userEmail={userEmail}
          userName={userName}
        />}


    </div>
  );
}
